import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ActivityItem, ActivityDescription, ActivityDescriptionEditor, descriptionPayload } from "@/components/activities/ActivityRich";
import { PageLoader } from "@/components/common/Loading";
import { StatusBadge } from "@/components/common/StatusBadge";
import { UserAvatar } from "@/components/common/UserAvatar";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { RecordDetailShell } from "@/components/records/RecordDetailShell";
import { RecordSections, applicableSections, DerivedGrid } from "@/components/records/RecordSections";
import { StageTracker } from "@/components/records/StageTracker";
import { RelationshipsPanel } from "@/components/crm/RelationshipsPanel";
import { MemberCombobox } from "@/components/support/MemberCombobox";
import { OutcomeDialog, clearOutcomeRequest } from "@/pages/people/PersonDetail";
import { User, Workflow, Save, X, StickyNote, Activity as ActIcon, CheckSquare, History, Clock, CalendarClock } from "lucide-react";
import { toast } from "sonner";

export default function OpportunityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canRecruiting, isManager: isMgr, isGroupAdmin } = useAuth();
  const isManager = isMgr || isGroupAdmin;
  const [searchParams, setSearchParams] = useSearchParams();
  const assignRef = useRef(null);
  const [assignHl, setAssignHl] = useState(false);
  const [o, setO] = useState(null);
  const [layout, setLayout] = useState(null);
  const [customFields, setCustomFields] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [candTypes, setCandTypes] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [fieldOpts, setFieldOpts] = useState({ priorities: [], interest_levels: [] });
  const [elig, setElig] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [data, setData] = useState({ notes: [], activities: [], tasks: [], timeline: [] });
  const [ctDialog, setCtDialog] = useState(null);
  const [pipeDialog, setPipeDialog] = useState(false);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});
  const [hideEmpty, setHideEmpty] = useState(true);
  const [saving, setSaving] = useState(false);
  const [schedule, setSchedule] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const loadAppointments = useCallback(() => api.get("/appointments", { params: { opportunity_id: id } }).then((r) => setAppointments(r.data.items)).catch(() => {}), [id]);
  useEffect(() => { loadAppointments(); }, [loadAppointments]);

  const statusLabel = (k) => statuses.find((s) => s.key === k)?.label || k;
  const ctLabel = (k) => candTypes.find((c) => c.key === k)?.label || k;

  const load = useCallback(async () => {
    try {
      const { data: opp } = await api.get(`/opportunities/${id}`);
      setO(opp); setForm(opp);
      const [st, ct, pl, tl, nt, ac, tk, ly, cf, fo] = await Promise.all([
        api.get("/statuses"), api.get("/candidate-types"), api.get("/pipelines"),
        api.get(`/timeline/opportunity/${id}`),
        api.get("/notes", { params: { opportunity_id: id } }), api.get("/activities", { params: { opportunity_id: id } }),
        api.get("/tasks", { params: { opportunity_id: id } }),
        api.get("/layouts", { params: { record_type: "opportunity" } }),
        api.get("/custom-fields", { params: { entity_type: "opportunity" } }),
        api.get("/opportunity-field-options"),
      ]);
      setFieldOpts(fo.data);
      setStatuses(st.data.items); setCandTypes(ct.data.items); setPipelines(pl.data.items.filter((p) => p.active !== false));
      setData({ notes: nt.data.notes, activities: ac.data.activities, tasks: tk.data.tasks, timeline: tl.data.events });
      setLayout(ly.data); setHideEmpty(ly.data.hide_empty_default !== false);
      setCustomFields(cf.data.items.filter((f) => !f.hidden));
      try { const og = await api.get("/organizations"); setOrgs(og.data.organizations); } catch {}
    } catch (e) { toast.error(apiError(e)); navigate("/recruiting"); }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  // Deep-link from the manager digest ("Review & Assign"): focus the recruiter control on live data.
  useEffect(() => {
    if (o && searchParams.get("assign") === "1" && assignRef.current) {
      assignRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      setAssignHl(true);
      if (o.assigned_recruiter_id) toast.message(`Already assigned to ${o.recruiter_name || "a recruiter"}. Update below if needed.`);
      const t = setTimeout(() => setAssignHl(false), 3500);
      searchParams.delete("assign");
      setSearchParams(searchParams, { replace: true });
      return () => clearTimeout(t);
    }
  }, [o]); // eslint-disable-line react-hooks/exhaustive-deps

  // District/Group-aware recruiter eligibility for the Assigned Recruiter control (managers only).
  useEffect(() => {
    if (!isManager) return;
    let active = true;
    api.get("/recruiting/eligible-recruiters", { params: { opportunity_id: id, scope: showAll ? "all" : "district" } })
      .then((r) => { if (active) setElig(r.data); })
      .catch(() => { if (active) setElig({ recruiters: [], district_name: null, can_show_more: false }); });
    return () => { active = false; };
  }, [id, showAll, isManager]);

  if (!o || !layout) return <PageLoader label="Loading opportunity" />;

  const patch = async (body, msg) => { try { await api.patch(`/opportunities/${id}`, body); toast.success(msg || "Updated"); load(); } catch (e) { toast.error(apiError(e)); } };
  const moveStage = async (sid) => { try { await api.post(`/opportunities/${id}/stage`, { stage_id: sid }); toast.success("Stage updated"); load(); } catch (e) { toast.error(apiError(e)); } };
  const transfer = async (orgId) => { try { await api.post(`/opportunities/${id}/transfer-org`, { organization_id: orgId }); toast.success("Transferred"); load(); } catch (e) { toast.error(apiError(e)); } };

  const save = async () => {
    setSaving(true);
    try {
      const { data: upd } = await api.patch(`/opportunities/${id}`, {
        source: form.source || "", source_id: form.source_id || "", priority: form.priority, interest_level: form.interest_level, custom_fields: form.custom_fields || {},
      });
      setO(upd); setForm(upd); setEdit(false); toast.success("Saved");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const stages = (o.pipeline?.stages || []).slice().sort((a, b) => a.order - b.order);
  const assignOpts = (() => {
    const list = [...((elig && elig.recruiters) || [])];
    if (o.assigned_recruiter_id && !list.some((r) => r.id === o.assigned_recruiter_id)) {
      list.unshift({ id: o.assigned_recruiter_id, name: `${o.recruiter_name || "Current recruiter"} (current)`, email: null });
    }
    return list;
  })();
  const statusOpts = statuses.filter((s) => s.active !== false || s.key === o.status).map((s) => [s.key, s.active === false ? `${s.label} (inactive)` : s.label]);
  const stageOpts = stages.map((s) => [s.id, s.name]);
  const coreOptions = { priority: fieldOpts.priorities, interest_level: fieldOpts.interest_levels };
  const setStatus = (v) => v !== o.status && patch({ status: v }, "Status updated");
  const recruiterPicker = (testId) => (
    <RecruiterPicker isManager={isManager} elig={elig} opts={assignOpts} o={o} testId={testId}
      onPick={(v) => patch({ assigned_recruiter_id: v }, "Recruiter assigned")} />
  );
  const customDefs = Object.fromEntries(customFields.map((f) => [f.name, { type: f.field_type, options: f.options }]));

  // inject unplaced opportunity custom fields
  let sections = [...(layout.sections || [])];
  const placed = new Set();
  sections.forEach((s) => (s.fields || []).forEach((f) => f.source === "custom" && placed.add(f.key)));
  const unplaced = customFields.filter((f) => !placed.has(f.name));
  if (unplaced.length) sections = [...sections, { key: "additional_information", title: "Additional Information", render: "fields", columns: 2, collapsible: true, conditional: {}, fields: unplaced.map((f) => ({ key: f.name, label: f.label, source: "custom", type: f.field_type, visible: true, width: "half" })) }];
  const shown = applicableSections(sections, { hasOpportunity: true, candidateType: o.candidate_type, classifications: [], canRecruiting });

  const systemRenderers = { relationships: () => <RelationshipsPanel bare recordType="opportunity" recordId={id} /> };

  const header = (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col lg:flex-row lg:items-start gap-4">
        <UserAvatar name={o.person_name} size={56} />
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-2xl font-bold text-slate-900" data-testid="opp-person-name">{o.person_name}
            {o.person_preferred && <span className="text-slate-400 font-normal text-lg"> ({o.person_preferred})</span>}</h1>
          <p className="text-sm text-slate-400 font-mono mt-0.5">{o.human_id} · {ctLabel(o.candidate_type) || "Unknown type"} · {o.pipeline_name || o.pipeline?.name || "Unknown pipeline"}</p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-sm" data-testid="opp-person-contact">
            {o.person_email ? <a href={`mailto:${o.person_email}`} className="text-[#006090] hover:underline break-all" data-testid="opp-person-email">{o.person_email}</a> : <span className="text-slate-400" data-testid="opp-person-email-missing">No email on file</span>}
            {o.person_mobile ? <a href={`tel:${o.person_mobile.replace(/[^0-9+]/g, "")}`} className="text-[#006090] hover:underline" data-testid="opp-person-mobile">{o.person_mobile}</a> : <span className="text-slate-400" data-testid="opp-person-mobile-missing">No mobile on file</span>}
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {o.current_stage_name && <span className="text-xs rounded-full bg-brand/10 text-brand px-2.5 py-0.5 font-medium">{o.current_stage_name}</span>}
            <StatusBadge status={o.status} label={statusLabel(o.status)} />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!edit ? (
            <>
              <Button variant="outline" onClick={() => setEdit(true)} data-testid="edit-opportunity">Edit</Button>
              <Button variant="outline" size="sm" onClick={() => setSchedule(true)} data-testid="schedule-interview-btn"><CalendarClock className="w-4 h-4 mr-1" /> Schedule Interview</Button>
              <Button variant="outline" size="sm" onClick={() => navigate(`/people/detail/${o.person_id}`)} data-testid="view-person"><User className="w-4 h-4 mr-1" /> View Person</Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => { setEdit(false); setForm(o); }}><X className="w-4 h-4 mr-1" /> Cancel</Button>
              <Button onClick={save} disabled={saving} data-testid="save-opportunity" className="bg-brand hover:bg-brand-strong"><Save className="w-4 h-4 mr-1" /> Save</Button>
            </>
          )}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
        <ActionSelect label="Stage" value={o.current_stage_id} testId="action-stage" onChange={moveStage} options={stageOpts}
          emptyText={`${o.current_stage_name || "Lead"} · choose a pipeline first`} />
        <ActionSelect label="Opportunity Status" value={o.status} testId="action-status" onChange={setStatus} options={statusOpts} />
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <p className="text-xs font-semibold text-slate-500 mb-1.5">Candidate Type</p>
          <Select value={o.candidate_type || ""} onValueChange={(v) => v !== o.candidate_type && setCtDialog({ candidate_type: v })}>
            <SelectTrigger data-testid="action-candidate-type"><SelectValue placeholder="Unknown" /></SelectTrigger>
            <SelectContent className="bg-white max-h-72">{candTypes.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div ref={assignRef} className={`rounded-lg border border-slate-200 bg-white p-3 transition-shadow ${assignHl ? "ring-2 ring-brand ring-offset-2" : ""}`} data-testid="assign-recruiter-target">
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <p className="text-xs font-semibold text-slate-500">Assigned Recruiter</p>
            {isManager && elig && <span className="text-[10px] font-medium text-slate-400 truncate max-w-[55%]" title={elig.district_name || "Unassigned District"} data-testid="assign-district-label">{elig.district_name || "Unassigned District"}</span>}
          </div>
          {recruiterPicker("action-recruiter")}
          {isManager && elig?.can_show_more && !showAll && (
            <button type="button" onClick={() => setShowAll(true)} className="mt-2 text-xs text-brand font-medium hover:underline" data-testid="show-other-recruiters">Show other authorized recruiters</button>
          )}
          {isManager && showAll && (
            <button type="button" onClick={() => setShowAll(false)} className="mt-2 text-xs text-slate-400 hover:underline" data-testid="show-district-recruiters">Show district only</button>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        <Button variant="outline" size="sm" onClick={() => setPipeDialog({})} data-testid="change-pipeline-button"><Workflow className="w-4 h-4 mr-1.5" /> Change Pipeline</Button>
      </div>
    </div>
  );

  const overview = (
    <>
      <RecruitingDetails o={o} pipelines={pipelines} stageOpts={stageOpts} statusOpts={statusOpts} ctLabel={ctLabel}
        onPipeline={(pid) => setPipeDialog({ pid })} onStage={moveStage} onStatus={setStatus} recruiter={recruiterPicker("details-recruiter")} />
      <RecordSections sections={shown} record={o} custom={o.custom_fields} form={form} setForm={setForm} coreOptions={coreOptions}
        customDefs={customDefs} orgs={orgs} edit={edit} hideEmpty={hideEmpty} systemRenderers={systemRenderers} />
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="font-display font-semibold text-sm mb-3">Transfer Organization</h3>
        <Select onValueChange={transfer}><SelectTrigger data-testid="action-transfer" className="max-w-sm"><SelectValue placeholder="Move to organization…" /></SelectTrigger>
          <SelectContent className="bg-white">{orgs.map((og) => <SelectItem key={og.id} value={og.id}>{og.name}</SelectItem>)}</SelectContent></Select>
      </div>
    </>
  );

  const related = [
    { key: "interviews", label: "Interviews", icon: CalendarClock, count: appointments.filter((a) => a.status !== "cancelled").length, node: <InterviewsPanel items={appointments} onSchedule={() => setSchedule(true)} reload={loadAppointments} /> },
    { key: "activities", label: "Activities", icon: ActIcon, count: data.activities.length, node: <ActivityPanel id={id} personId={o.person_id} items={data.activities} reload={load} /> },
    { key: "tasks", label: "Tasks", icon: CheckSquare, count: data.tasks.length, node: <TaskPanel id={id} personId={o.person_id} items={data.tasks} reload={load} /> },
    { key: "notes", label: "Notes", icon: StickyNote, count: data.notes.length, node: <NotePanel id={id} personId={o.person_id} items={data.notes} reload={load} /> },
    { key: "history", label: "History", icon: History, node: <HistoryPanel o={o} statusLabel={statusLabel} ctLabel={ctLabel} /> },
  ];

  return (
    <>
      <RecordDetailShell
        backLabel="Recruiting" onBack={() => navigate("/recruiting")}
        header={header}
        stageTracker={<StageTracker stages={stages} currentStageId={o.current_stage_id} onMove={canRecruiting ? moveStage : undefined} />}
        overview={overview}
        timeline={<TimelinePanel events={data.timeline} />}
        related={related}
        hideEmpty={hideEmpty} onToggleHideEmpty={setHideEmpty} showHideEmpty={!edit}
      />
      {ctDialog && <ChangeCandidateTypeDialog oid={id} target={ctDialog.candidate_type} current={o} ctLabel={ctLabel} onClose={() => setCtDialog(null)} onDone={() => { setCtDialog(null); load(); }} />}
      {pipeDialog && <ChangePipelineDialog oid={id} pipelines={pipelines} current={o} initialPid={pipeDialog.pid} onClose={() => setPipeDialog(false)} onDone={() => { setPipeDialog(false); load(); }} />}
      {schedule && <ScheduleInterviewDialog opportunityId={id} personName={o.person_name} onClose={() => setSchedule(false)} onDone={() => { setSchedule(false); loadAppointments(); }} />}
    </>
  );
}

function ActionSelect({ label, value, options, onChange, testId, emptyText }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <p className="text-xs font-semibold text-slate-500 mb-1.5">{label}</p>
      {!options.length && emptyText ? <p className="text-sm text-slate-500 py-2" data-testid={`${testId}-empty`}>{emptyText}</p> : (
      <Select value={value || ""} onValueChange={onChange}><SelectTrigger data-testid={testId}><SelectValue placeholder="Select" /></SelectTrigger>
        <SelectContent className="bg-white max-h-72">{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>)}
    </div>
  );
}

function RecruiterPicker({ isManager, elig, opts, o, onPick, testId }) {
  if (!isManager) return <p className="text-sm text-slate-700" data-testid={testId === "action-recruiter" ? "assigned-recruiter-readonly" : `${testId}-readonly`}>{o.recruiter_name || "Unassigned"}</p>;
  if (!opts.length) return <p className="text-xs text-amber-600" data-testid={testId === "action-recruiter" ? "no-eligible-recruiters" : `${testId}-none`}>No eligible recruiters for this district yet. Add a recruiter to this district{elig?.can_show_more ? ", or show other authorized recruiters." : "."}</p>;
  return (
    <MemberCombobox members={opts.map((r) => ({ member_id: r.id, name: r.name, email: r.email }))} value={o.assigned_recruiter_id || null}
      onChange={(v) => v && v !== o.assigned_recruiter_id && onPick(v)} testId={testId} placeholder="Unassigned"
      emptyText="No eligible recruiters." clearable={false} ariaLabel="Choose recruiter" />
  );
}

function DetailField({ label, children, testId }) {
  return (
    <div data-testid={testId}>
      <p className="text-xs font-semibold text-slate-500 mb-1">{label}</p>{children}
    </div>
  );
}

function RecruitingDetails({ o, pipelines, stageOpts, statusOpts, ctLabel, onPipeline, onStage, onStatus, recruiter }) {
  const inactiveCurrent = o.pipeline_id && !pipelines.some((p) => p.id === o.pipeline_id);
  const ro = (v) => <p className="text-sm text-slate-800 mt-0.5">{v || "—"}</p>;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="recruiting-details">
      <h3 className="font-display font-semibold text-sm mb-4">Recruiting Details</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
        <DetailField label="Pipeline" testId="details-pipeline">
          <Select value={o.pipeline_id || "__none__"} onValueChange={(v) => v !== "__none__" && v !== o.pipeline_id && onPipeline(v)}>
            <SelectTrigger data-testid="details-pipeline-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              {!o.pipeline_id && <SelectItem value="__none__" disabled>Unknown</SelectItem>}
              {inactiveCurrent && <SelectItem value={o.pipeline_id} disabled>{o.pipeline_name || "Current pipeline"} (inactive)</SelectItem>}
              {pipelines.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`details-pipeline-opt-${p.id}`}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </DetailField>
        <DetailField label="Current Stage" testId="details-stage">
          {stageOpts.length ? (
            <Select value={o.current_stage_id || ""} onValueChange={(v) => v !== o.current_stage_id && onStage(v)}>
              <SelectTrigger data-testid="details-stage-select"><SelectValue placeholder="Select stage" /></SelectTrigger>
              <SelectContent className="bg-white max-h-72">{stageOpts.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          ) : <p className="text-sm text-slate-500 py-2" data-testid="details-stage-unknown">{o.current_stage_name || "Lead"} · choose a pipeline first</p>}
        </DetailField>
        <DetailField label="Opportunity Status" testId="details-status">
          <Select value={o.status || ""} onValueChange={onStatus}>
            <SelectTrigger data-testid="details-status-select"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="bg-white max-h-72">{statusOpts.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </DetailField>
        <DetailField label="Recruiter" testId="details-recruiter-field">{recruiter}</DetailField>
        <DetailField label="Candidate Type">{ro(ctLabel(o.candidate_type) || "Unknown")}</DetailField>
        <DetailField label="Organization">{ro(o.organization_name)}</DetailField>
        <DetailField label="Opened">{ro(o.open_date && new Date(o.open_date).toLocaleDateString())}</DetailField>
        <DetailField label="Last Activity">{ro(o.last_activity_at && new Date(o.last_activity_at).toLocaleString())}</DetailField>
      </div>
    </div>
  );
}

function PanelCard({ title, children, testId }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={testId}>
      <h3 className="font-display font-semibold text-slate-900 text-sm mb-4">{title}</h3>{children}
    </div>
  );
}

function fmtWhen(s) {
  if (!s) return "—";
  const d = new Date(s.length <= 10 ? s + "T00:00:00" : s);
  return d.toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

const IV_KIND = { initial: "Initial Interview", final: "Final Interview", other: "Interview", "": "Interview" };

function InterviewsPanel({ items, onSchedule, reload }) {
  const upcoming = items.filter((a) => a.status !== "cancelled").sort((a, b) => (a.start || "").localeCompare(b.start || ""));
  const [outcomeFor, setOutcomeFor] = useState(null);
  const del = async (a) => { try { await api.delete(`/appointments/${a.id}`); reload(); toast.success("Removed"); } catch (e) { toast.error(apiError(e)); } };
  return (
    <PanelCard title="Interviews & Appointments" testId="interviews-panel">
      <div className="flex justify-end mb-3">
        <Button size="sm" className="bg-brand hover:bg-brand-strong" onClick={onSchedule} data-testid="panel-schedule-interview"><CalendarClock className="w-4 h-4 mr-1.5" />Schedule Interview</Button>
      </div>
      {upcoming.length ? (
        <div className="space-y-2">
          {upcoming.map((a) => (
            <div key={a.id} className="flex items-center gap-3 rounded-lg border border-slate-200 p-3" data-testid={`appt-${a.id}`}>
              <div className={`w-1.5 h-10 rounded-full ${a.status === "completed" ? "bg-emerald-300" : "bg-brand/50"}`} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-800">{a.title || IV_KIND[a.interview_kind || ""]}</p>
                <p className="text-xs text-slate-400">{fmtWhen(a.start)}{a.outcome_label ? "" : a.status === "completed" ? " · Completed" : ""}</p>
                {a.outcome_label && <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: `${a.outcome_color || "#64748b"}22`, color: a.outcome_color || "#475569" }} data-testid={`appt-outcome-${a.id}`}>{a.outcome_label}</span>}
                {a.meeting_link && <a href={a.meeting_link} target="_blank" rel="noreferrer" className="text-xs text-brand">Join link</a>}
              </div>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setOutcomeFor({ ...a, appointment_type: a.title || IV_KIND[a.interview_kind || ""] })} data-testid={`appt-log-outcome-${a.id}`}>{a.outcome_label ? "Update Outcome" : "Log Outcome"}</Button>
              {a.outcome_label && <Button size="sm" variant="ghost" className="h-7 text-xs text-rose-600" onClick={() => clearOutcomeRequest(a, reload)} data-testid={`appt-clear-outcome-${a.id}`}>Clear Outcome</Button>}
              <Button size="sm" variant="ghost" className="h-7 text-xs text-rose-600" onClick={() => del(a)} data-testid={`appt-delete-${a.id}`}>Remove</Button>
            </div>
          ))}
        </div>
      ) : <EmptyState icon={CalendarClock} title="No interviews scheduled" description="Schedule an interview to add it to the calendar and dashboard." />}
      {outcomeFor && <OutcomeDialog meeting={outcomeFor} onClose={() => setOutcomeFor(null)} onSaved={() => { setOutcomeFor(null); reload(); }} />}
    </PanelCard>
  );
}

function ScheduleInterviewDialog({ opportunityId, personName, onClose, onDone }) {
  const [f, setF] = useState({ interview_kind: "initial", title: "", date: "", time: "10:00", meeting_link: "", location: "", notes: "", reminder_enabled: false, reminder_lead_minutes: 15 });
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState({ available: false, items: [] });
  useEffect(() => {
    if (!f.date) { setBusy({ available: false, items: [] }); return; }
    let active = true;
    api.get("/calendar/outlook", { params: { start: f.date, end: f.date } })
      .then((r) => { if (active) setBusy(r.data); })
      .catch(() => { if (active) setBusy({ available: false, items: [] }); });
    return () => { active = false; };
  }, [f.date]);
  const t = (s) => (s || "").slice(11, 16);
  const sel = f.time || "10:00";
  const conflict = (busy.items || []).some((b) => { const bs = t(b.start); const be = t(b.end) || bs; return be > bs ? (sel >= bs && sel < be) : false; });
  const save = async () => {
    if (!f.date) { toast.error("Pick a date"); return; }
    setSaving(true);
    try {
      await api.post("/appointments", {
        opportunity_id: opportunityId, type: "interview", interview_kind: f.interview_kind,
        title: f.title || IV_KIND[f.interview_kind], start: `${f.date}T${f.time || "10:00"}:00`,
        meeting_link: f.meeting_link, location: f.location, notes: f.notes, reminder_enabled: f.reminder_enabled,
      });
      toast.success("Interview scheduled"); onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="schedule-interview-dialog">
        <DialogHeader><DialogTitle>Schedule Interview</DialogTitle>
          <DialogDescription>{personName}</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-600">Type</label>
            <Select value={f.interview_kind} onValueChange={(v) => setF({ ...f, interview_kind: v })}>
              <SelectTrigger data-testid="iv-kind"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="initial">Initial Interview</SelectItem><SelectItem value="final">Final Interview</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-semibold text-slate-600">Date</label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="iv-date" /></div>
            <div><label className="text-xs font-semibold text-slate-600">Time</label><Input type="time" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} data-testid="iv-time" /></div>
          </div>
          {f.date && busy.available && (
            <div className={`rounded-lg border p-2.5 text-xs ${conflict ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-slate-50"}`} data-testid="iv-busy-panel">
              <p className="font-semibold text-slate-600 mb-1">Your Outlook that day</p>
              {conflict && <p className="text-amber-700 font-medium mb-1" data-testid="iv-busy-conflict">⚠ The selected time overlaps a busy block below.</p>}
              {(busy.items || []).length ? (
                <ul className="space-y-0.5">
                  {busy.items.map((b) => (
                    <li key={b.id} className="flex items-center gap-1.5 text-slate-600">
                      <Clock className="w-3 h-3 shrink-0 text-slate-400" />
                      <span className="tabular-nums">{b.all_day ? "All day" : `${t(b.start)}–${t(b.end)}`}</span>
                      <span className="truncate">· {b.private ? "Busy" : b.title}</span>
                    </li>
                  ))}
                </ul>
              ) : <p className="text-slate-400">No conflicts — you're free all day.</p>}
            </div>
          )}
          <div><label className="text-xs font-semibold text-slate-600">Title (optional)</label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder={IV_KIND[f.interview_kind]} data-testid="iv-title" /></div>
          <div><label className="text-xs font-semibold text-slate-600">Meeting link (optional)</label><Input value={f.meeting_link} onChange={(e) => setF({ ...f, meeting_link: e.target.value })} placeholder="https://teams.microsoft.com/…" data-testid="iv-link" /></div>
          <div><label className="text-xs font-semibold text-slate-600">Location (optional)</label><Input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="iv-location" /></div>
          <label className="flex items-center gap-2 text-sm text-slate-700 pt-1">
            <input type="checkbox" checked={f.reminder_enabled} onChange={(e) => setF({ ...f, reminder_enabled: e.target.checked })} data-testid="iv-reminder" />
            Send a reminder to me and the candidate
          </label>
          {f.reminder_enabled && (
            <div>
              <label className="text-xs font-semibold text-slate-600">Remind</label>
              <Select value={String(f.reminder_lead_minutes)} onValueChange={(v) => setF({ ...f, reminder_lead_minutes: Number(v) })}>
                <SelectTrigger data-testid="iv-reminder-lead"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="15">15 minutes before</SelectItem>
                  <SelectItem value="30">30 minutes before</SelectItem>
                  <SelectItem value="45">45 minutes before</SelectItem>
                  <SelectItem value="60">1 hour before</SelectItem>
                  <SelectItem value="1440">1 day before</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={saving} className="bg-brand hover:bg-brand-strong" data-testid="iv-save">Schedule</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function NotePanel({ id, personId, items, reload }) {
  const [text, setText] = useState(""); const [vis, setVis] = useState("recruiting_team");
  const add = async () => { if (!text) return; try { await api.post("/notes", { opportunity_id: id, person_id: personId, text, visibility: vis }); setText(""); reload(); toast.success("Note added"); } catch (e) { toast.error(apiError(e)); } };
  return (
    <PanelCard title="Notes" testId="opp-notes-panel">
      <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 mb-4">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a note…" data-testid="note-input" />
        <div className="flex items-center justify-between mt-2">
          <Select value={vis} onValueChange={setVis}><SelectTrigger className="w-44" data-testid="note-visibility"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white"><SelectItem value="private">Private</SelectItem><SelectItem value="recruiting_team">Recruiting Team</SelectItem><SelectItem value="organization">Organization</SelectItem></SelectContent></Select>
          <Button onClick={add} data-testid="note-submit" className="bg-brand hover:bg-brand-strong">Add Note</Button>
        </div>
      </div>
      {items.length ? items.map((n) => (
        <div key={n.id} className="rounded-lg border border-slate-200 bg-white p-4 mb-2" data-testid={`note-${n.id}`}>
          <div className="flex items-center justify-between"><div className="flex items-center gap-2"><UserAvatar name={n.author_name} size={24} /><span className="text-sm font-medium text-slate-700">{n.author_name}</span><StatusBadge status="inactive" label={(n.visibility || "").replace("_", " ")} /></div>
            <span className="text-xs text-slate-400">{new Date(n.created_at).toLocaleString()}</span></div>
          <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{n.text}</p>
        </div>
      )) : <EmptyState icon={StickyNote} title="No notes" description="Add the first note." />}
    </PanelCard>
  );
}

function ActivityPanel({ id, personId, items, reload }) {
  const [f, setF] = useState({ type: "Phone Call", subject: "", description: "" });
  const [formKey, setFormKey] = useState(0);
  const add = async () => { try { await api.post("/activities", { type: f.type, subject: f.subject, ...descriptionPayload(f.description), opportunity_id: id, person_id: personId }); setF({ type: "Phone Call", subject: "", description: "" }); setFormKey((k) => k + 1); reload(); toast.success("Activity logged"); } catch (e) { toast.error(apiError(e)); } };
  return (
    <PanelCard title="Activities" testId="opp-activities-panel">
      <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 mb-4 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}><SelectTrigger data-testid="activity-type"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{["Phone Call", "Meeting", "Interview", "Text Conversation", "Email Conversation", "Networking Interaction", "Referral Contact", "Other"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
          <Input placeholder="Subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="activity-subject" />
        </div>
        <ActivityDescriptionEditor key={formKey} value={f.description} onChange={(h) => setF((s) => ({ ...s, description: h }))} />
        <div className="flex justify-end"><Button onClick={add} data-testid="activity-submit" className="bg-brand hover:bg-brand-strong">Log Activity</Button></div>
      </div>
      {items.length ? items.map((a) => <ActivityItem key={a.id} a={a} onSaved={reload} />) : <EmptyState icon={ActIcon} title="No activities" description="Log calls, meetings and interviews here." />}
    </PanelCard>
  );
}

function TaskPanel({ id, personId, items, reload }) {
  const [title, setTitle] = useState(""); const [due, setDue] = useState(""); const [priority, setPriority] = useState("normal");
  const add = async () => { if (!title) return; try { await api.post("/tasks", { title, due_date: due || null, priority, opportunity_id: id, person_id: personId }); setTitle(""); setDue(""); reload(); toast.success("Task created"); } catch (e) { toast.error(apiError(e)); } };
  const complete = async (t) => { try { await api.patch(`/tasks/${t.id}`, { status: t.status === "completed" ? "open" : "completed" }); reload(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <PanelCard title="Tasks" testId="opp-tasks-panel">
      <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 mb-4 flex flex-wrap gap-2 items-end">
        <Input placeholder="Task title" value={title} onChange={(e) => setTitle(e.target.value)} className="flex-1 min-w-[180px]" data-testid="task-title" />
        <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="w-40" data-testid="task-due" />
        <Select value={priority} onValueChange={setPriority}><SelectTrigger className="w-32"><SelectValue /></SelectTrigger><SelectContent className="bg-white">{["low", "normal", "high", "urgent"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
        <Button onClick={add} data-testid="task-submit" className="bg-brand hover:bg-brand-strong">Add Task</Button>
      </div>
      {items.length ? items.map((t) => (
        <div key={t.id} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3 mb-2" data-testid={`task-${t.id}`}>
          <input type="checkbox" checked={t.status === "completed"} onChange={() => complete(t)} data-testid={`task-complete-${t.id}`} className="w-4 h-4" />
          <div className="flex-1"><p className={`text-sm ${t.status === "completed" ? "line-through text-slate-400" : "text-slate-700"}`}>{t.title}</p>
            <p className="text-xs text-slate-400">{t.due_date ? `Due ${new Date(t.due_date).toLocaleDateString()}` : "No due date"}</p></div>
          <StatusBadge status={t.priority === "high" || t.priority === "urgent" ? "pending" : "inactive"} label={t.priority} />
        </div>
      )) : <EmptyState icon={CheckSquare} title="No tasks" description="Create a follow-up task." />}
    </PanelCard>
  );
}

function HistoryPanel({ o, statusLabel, ctLabel }) {
  const rows = [];
  (o.stage_history || []).forEach((h) => rows.push({ t: h.entered_at, d: `Stage → “${h.stage_name}”`, u: h.moved_by_name }));
  (o.status_history || []).forEach((h) => rows.push({ t: h.at, d: `Status: ${h.from ? statusLabel(h.from) : "—"} → ${statusLabel(h.to)}`, u: h.by_name }));
  (o.candidate_type_history || []).forEach((h) => rows.push({ t: h.at, d: `Candidate Type: ${h.from ? ctLabel(h.from) : "—"} → ${ctLabel(h.to)}`, u: h.by_name }));
  (o.pipeline_history || []).forEach((h) => rows.push({ t: h.at, d: `Pipeline: ${h.from_name || "—"} → ${h.to_name || "—"}`, u: h.by_name }));
  (o.org_history || []).forEach((h) => rows.push({ t: h.at, d: `Organization transferred`, u: h.by_name || "" }));
  (o.recruiter_history || []).forEach((h) => rows.push({ t: h.at, d: `Assigned recruiter changed`, u: "" }));
  rows.sort((a, b) => (b.t || "").localeCompare(a.t || ""));
  if (!rows.length) return <EmptyState icon={History} title="No history yet" />;
  return (
    <div className="space-y-2" data-testid="opp-history">
      {rows.map((r, i) => (
        <div key={i} className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-3 text-sm">
          <span className="text-slate-700">{r.d}</span>
          <span className="text-xs text-slate-400">{r.u} · {r.t ? new Date(r.t).toLocaleString() : ""}</span>
        </div>
      ))}
    </div>
  );
}

function TimelinePanel({ events }) {
  if (!events?.length) return <EmptyState icon={Clock} title="No timeline events" description="Activity will appear here." />;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm space-y-3" data-testid="opp-timeline">
      {events.map((e) => (
        <div key={e.id} className="flex gap-3">
          <div className="w-2 h-2 rounded-full bg-brand mt-1.5 shrink-0" />
          <div className="flex-1 border-b border-slate-100 pb-3">
            <p className="text-sm text-slate-700">{e.description}</p>
            {(e.detail_html || e.detail_text) && <div className="mt-1 rounded-md bg-slate-50 px-3 py-2" data-testid={`timeline-detail-${e.id}`}><ActivityDescription html={e.detail_html} text={e.detail_text} /></div>}
            <p className="text-xs text-slate-400">{e.user_name} · {e.created_at ? new Date(e.created_at).toLocaleString() : ""}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function ChangeCandidateTypeDialog({ oid, target, current, ctLabel, onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [stageId, setStageId] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get(`/opportunities/${oid}/candidate-type-preview`, { params: { candidate_type: target } })
      .then((r) => { setPreview(r.data); setStageId(r.data.suggested_stage_id || ""); })
      .catch((e) => toast.error(apiError(e)));
  }, [oid, target]);
  const confirm = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/opportunities/${oid}/change-candidate-type`, { candidate_type: target, pipeline_id: preview?.pipeline?.id || null, stage_id: preview?.pipeline_changes ? stageId : null });
      const pc = data?.program_change;
      if (pc?.changed) toast.success(`Program changed from ${pc.from} to ${pc.to} · ${pc.carried.length} completed requirement(s) carried forward${pc.review?.length ? ` · ${pc.review.length} to review` : ""}`);
      else if (pc?.review) toast.warning(pc.review);
      else toast.success("Candidate Type updated");
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const stages = (preview?.pipeline?.stages || []).slice().sort((a, b) => a.order - b.order);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white" data-testid="change-candidate-type-dialog">
        <DialogHeader><DialogTitle>Change Candidate Type</DialogTitle><DialogDescription>Changing to <b>{ctLabel(target)}</b>. History is preserved.</DialogDescription></DialogHeader>
        {!preview ? <PageLoader /> : (
          <div className="space-y-3 text-sm">
            <div className="rounded-md bg-slate-50 border border-slate-200 p-3">
              <p><span className="text-slate-500">Current pipeline:</span> {current.pipeline_name || current.pipeline?.name || "—"} · <span className="text-slate-500">stage</span> {current.current_stage_name}</p>
              <p className="mt-1"><span className="text-slate-500">New default pipeline:</span> <b>{preview.pipeline?.name || "—"}</b></p>
            </div>
            {preview.pipeline_changes ? (
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-1.5">Select the current stage in the new pipeline</p>
                <Select value={stageId} onValueChange={setStageId}><SelectTrigger data-testid="ct-stage-select"><SelectValue placeholder="Select stage" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-72">{stages.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select>
                <p className="text-xs text-slate-400 mt-1.5">Previous pipeline &amp; stage history will be preserved.</p>
              </div>
            ) : <p className="text-slate-500">Pipeline stays the same; only the candidate type changes.</p>}
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={confirm} disabled={saving || (preview?.pipeline_changes && !stageId)} data-testid="ct-confirm" className="bg-brand hover:bg-brand-strong">Confirm</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChangePipelineDialog({ oid, pipelines, current, initialPid, onClose, onDone }) {
  const [pid, setPid] = useState(initialPid || "");
  const [stageId, setStageId] = useState("");
  const [saving, setSaving] = useState(false);
  const target = pipelines.find((p) => p.id === pid);
  const stages = (target?.stages || []).slice().sort((a, b) => a.order - b.order);
  useEffect(() => {
    if (!target) { setStageId(""); return; }
    const match = stages.find((s) => s.name.toLowerCase() === (current.current_stage_name || "").toLowerCase());
    setStageId((match || stages[0])?.id || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pid]);
  const confirm = async () => {
    if (!pid) return;
    setSaving(true);
    try { await api.post(`/opportunities/${oid}/change-pipeline`, { pipeline_id: pid, stage_id: stageId }); toast.success("Pipeline changed"); onDone(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white" data-testid="change-pipeline-dialog">
        <DialogHeader><DialogTitle>Change Pipeline</DialogTitle><DialogDescription>Move this opportunity to a different pipeline. Stage &amp; pipeline history are preserved.</DialogDescription></DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <p className="text-xs font-semibold text-slate-600 mb-1.5">Target pipeline</p>
            <Select value={pid} onValueChange={setPid}><SelectTrigger data-testid="pipe-select"><SelectValue placeholder="Select pipeline" /></SelectTrigger>
              <SelectContent className="bg-white">{pipelines.filter((p) => p.id !== current.pipeline_id).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select>
          </div>
          {target && (
            <div>
              <p className="text-xs font-semibold text-slate-600 mb-1.5">Current stage in new pipeline</p>
              <Select value={stageId} onValueChange={setStageId}><SelectTrigger data-testid="pipe-stage-select"><SelectValue placeholder="Select stage" /></SelectTrigger>
                <SelectContent className="bg-white max-h-72">{stages.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select>
            </div>
          )}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={confirm} disabled={saving || !pid || !stageId} data-testid="pipe-confirm" className="bg-brand hover:bg-brand-strong">Confirm</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
